/**
 * ADMIN TODO DASHBOARD
 * 
 * Centralized task management dashboard for administrators.
 * Shows all pending tasks, allows task creation, assignment, and tracking.
 */

import React, { useState, useEffect } from 'react'
import { adminTaskService, AdminTask, TaskStatistics, TaskFilters } from '../../lib/services/adminTaskService'

interface AdminTodoDashboardProps {
  currentUser: any
}

const AdminTodoDashboard: React.FC<AdminTodoDashboardProps> = ({ currentUser }) => {
  const [tasks, setTasks] = useState<AdminTask[]>([])
  const [statistics, setStatistics] = useState<TaskStatistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'in_progress' | 'completed'>('pending')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [filters, setFilters] = useState<TaskFilters>({
    status: ['pending']
  })

  // New task form
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    assigned_to_role: '',
    due_date: ''
  })

  useEffect(() => {
    loadData()
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000)
    return () => clearInterval(interval)
  }, [filters])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Sync system tasks first
      await adminTaskService.syncSystemTasks()
      
      // Load tasks and statistics
      const [tasksResult, stats] = await Promise.all([
        adminTaskService.getTasks(filters, 100, 0),
        adminTaskService.getTaskStatistics()
      ])

      setTasks(tasksResult.tasks)
      setStatistics(stats)
    } catch (error) {
      console.error('Error loading admin tasks:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab)
    
    if (tab === 'all') {
      setFilters({ ...filters, status: undefined })
    } else {
      setFilters({ ...filters, status: [tab] })
    }
  }

  const handleCreateTask = async () => {
    try {
      if (!newTask.title) {
        alert('Please enter a task title')
        return
      }

      await adminTaskService.createTask({
        title: newTask.title,
        description: newTask.description,
        task_type: 'manual',
        priority: newTask.priority,
        assigned_to_role: newTask.assigned_to_role || undefined,
        due_date: newTask.due_date || undefined,
        created_by_user_id: currentUser?.adminUser?.id
      })

      // Reset form
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        assigned_to_role: '',
        due_date: ''
      })
      setShowCreateModal(false)
      
      // Reload data
      loadData()
    } catch (error) {
      console.error('Error creating task:', error)
      alert('Failed to create task')
    }
  }

  const handleUpdateStatus = async (taskId: string, newStatus: string) => {
    try {
      await adminTaskService.updateTaskStatus(taskId, newStatus, currentUser?.adminUser?.id)
      loadData()
    } catch (error) {
      console.error('Error updating task status:', error)
      alert('Failed to update task status')
    }
  }

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return

    try {
      await adminTaskService.deleteTask(taskId)
      loadData()
    } catch (error) {
      console.error('Error deleting task:', error)
      alert('Failed to delete task')
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/20 text-red-400 border-red-500/30'
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30'
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      default: return 'bg-gray-500/20 text-white border-gray-500/30'
    }
  }

  const getSourceTypeIcon = (sourceType?: string) => {
    switch (sourceType) {
      case 'payment_pending': return '💰'
      case 'withdrawal_pending': return '🏦'
      case 'kyc_pending': return '🔍'
      case 'support_ticket': return '💬'
      case 'user_registration': return '👤'
      case 'certificate_pending': return '📜'
      default: return '📋'
    }
  }

  const getSourceTypeLabel = (sourceType?: string) => {
    switch (sourceType) {
      case 'payment_pending': return 'Payment Review'
      case 'withdrawal_pending': return 'Withdrawal Processing'
      case 'kyc_pending': return 'KYC Verification'
      case 'support_ticket': return 'Support Ticket'
      case 'user_registration': return 'New User'
      case 'certificate_pending': return 'Certificate Approval'
      case 'custom': return 'Custom Task'
      default: return 'Task'
    }
  }

  if (loading && !statistics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading tasks...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">📋 Admin Task Dashboard</h1>
          <p className="text-white mt-1">Manage and track all administrative tasks</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
        >
          ➕ Create Task
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-white text-sm">Pending Tasks</div>
            <div className="text-3xl font-bold text-yellow-400 mt-1">{statistics.pending}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-white text-sm">In Progress</div>
            <div className="text-3xl font-bold text-blue-400 mt-1">{statistics.in_progress}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-white text-sm">Completed</div>
            <div className="text-3xl font-bold text-green-400 mt-1">{statistics.completed}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-white text-sm">Overdue</div>
            <div className="text-3xl font-bold text-red-400 mt-1">{statistics.overdue}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {(['all', 'pending', 'in_progress', 'completed'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === tab
                ? 'border-yellow-500 text-yellow-500'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            {tab === 'all' ? 'All Tasks' : tab.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            {statistics && tab !== 'all' && (
              <span className="ml-2 px-2 py-0.5 bg-gray-700 rounded-full text-sm">
                {statistics[tab as keyof typeof statistics] as number}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tasks List */}
      <div className="space-y-3">
        {tasks.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
            <div className="text-white text-lg">No tasks found</div>
            <p className="text-gray-500 text-sm mt-2">All caught up! 🎉</p>
          </div>
        ) : (
          tasks.map(task => (
            <div
              key={task.id}
              className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{getSourceTypeIcon(task.source_type)}</span>
                    <div>
                      <h3 className="text-white font-semibold">{task.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-sm font-medium border ${getPriorityColor(task.priority)}`}>
                          {task.priority.toUpperCase()}
                        </span>
                        {task.source_type && (
                          <span className="px-2 py-0.5 rounded text-sm bg-gray-700 text-gray-300">
                            {getSourceTypeLabel(task.source_type)}
                          </span>
                        )}
                        {task.assigned_to_role && (
                          <span className="px-2 py-0.5 rounded text-sm bg-blue-500/20 text-blue-400">
                            👤 {task.assigned_to_role}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {task.description && (
                    <p className="text-white text-sm mt-2">{task.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <span>Created: {new Date(task.created_at).toLocaleDateString()}</span>
                    {task.due_date && (
                      <span className={new Date(task.due_date) < new Date() ? 'text-red-400' : ''}>
                        Due: {new Date(task.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  {task.status === 'pending' && (
                    <button
                      onClick={() => handleUpdateStatus(task.id, 'in_progress')}
                      className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 text-sm"
                    >
                      Start
                    </button>
                  )}
                  {task.status === 'in_progress' && (
                    <button
                      onClick={() => handleUpdateStatus(task.id, 'completed')}
                      className="px-3 py-1 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 text-sm"
                    >
                      Complete
                    </button>
                  )}
                  {task.task_type === 'manual' && (
                    <button
                      onClick={() => handleDeleteTask(task.id)}
                      className="px-3 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 text-sm"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 border border-gray-700">
            <h2 className="text-2xl font-bold text-white mb-4">Create New Task</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Task Title *
                </label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="Enter task title..."
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  rows={4}
                  placeholder="Enter task description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Priority
                  </label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Assign to Role
                  </label>
                  <select
                    value={newTask.assigned_to_role}
                    onChange={(e) => setNewTask({ ...newTask, assigned_to_role: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  >
                    <option value="">Select role...</option>
                    <option value="CEO">CEO</option>
                    <option value="CMO">CMO</option>
                    <option value="CTO">CTO</option>
                    <option value="COO">COO</option>
                    <option value="Admin">Admin</option>
                    <option value="Support Agent">Support Agent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTask}
                className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminTodoDashboard

